import { useEffect, useMemo, useRef, useState } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { globalSearch } from "@/api/search";
import { useAuthStore } from "@/store/authStore";

type Hit =
  | { kind: "practitioner"; id: string; primary: string; secondary?: string }
  | { kind: "organization"; id: string; primary: string; secondary?: string }
  | { kind: "skill"; id: string; primary: string; secondary?: string };

const RECENTS_KEY = "cortex.cmdk.recents";
const RECENTS_MAX = 5;

function loadRecents(): Hit[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (h) =>
        h &&
        typeof h === "object" &&
        typeof h.id === "string" &&
        typeof h.primary === "string" &&
        ["practitioner", "organization", "skill"].includes(h.kind),
    ) as Hit[];
  } catch {
    return [];
  }
}

function pushRecent(hit: Hit): Hit[] {
  const current = loadRecents().filter(
    (h) => !(h.kind === hit.kind && h.id === hit.id),
  );
  const next = [hit, ...current].slice(0, RECENTS_MAX);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage can be unavailable (private browsing, quota) — silent
  }
  return next;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState<Hit[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Global hotkey: Cmd-K / Ctrl-K
  useEffect(() => {
    if (!accessToken) return;
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [accessToken]);

  useEffect(() => {
    if (open) {
      setActive(0);
      setRecents(loadRecents());
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQ("");
    }
  }, [open]);

  const debouncedQ = useDebounced(q, 180);

  const search = useQuery({
    queryKey: ["global-search", debouncedQ],
    queryFn: () => globalSearch(debouncedQ),
    enabled: Boolean(debouncedQ),
    staleTime: 30_000,
  });

  const hits: Hit[] = useMemo(() => {
    if (!search.data) return [];
    return [
      ...search.data.practitioners.map(
        (p): Hit => ({
          kind: "practitioner",
          id: p.id,
          primary: p.display_name,
          secondary: p.rpps ? `RPPS ${p.rpps}` : undefined,
        }),
      ),
      ...search.data.organizations.map(
        (o): Hit => ({
          kind: "organization",
          id: o.id,
          primary: o.name,
          secondary: o.code,
        }),
      ),
      ...search.data.skills.map(
        (s): Hit => ({
          kind: "skill",
          id: s.id,
          primary: s.label,
          secondary: s.code,
        }),
      ),
    ];
  }, [search.data]);

  const go = (hit: Hit) => {
    pushRecent(hit);
    setOpen(false);
    if (hit.kind === "practitioner") navigate(`/practitioners?q=${encodeURIComponent(hit.primary)}`);
    else if (hit.kind === "organization") navigate(`/organizations?q=${encodeURIComponent(hit.secondary ?? "")}`);
    else navigate("/admin/skills");
  };

  const currentList: Hit[] = debouncedQ ? hits : recents;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(currentList.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && currentList[active]) {
      e.preventDefault();
      go(currentList[active]);
    }
  };

  const iconFor = (kind: Hit["kind"]) => {
    if (kind === "practitioner") return <LocalHospitalIcon fontSize="small" />;
    if (kind === "organization") return <BusinessIcon fontSize="small" />;
    return <PsychologyIcon fontSize="small" />;
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { mt: { xs: 4, sm: 12 }, alignSelf: "flex-start" } } }}
    >
      <Box sx={{ p: 1.5 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          placeholder={t("cmdk.placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Chip size="small" variant="outlined" label="esc" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {!debouncedQ && recents.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, py: 0.5, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {t("cmdk.recent")}
          </Typography>
          <List sx={{ pt: 0, pb: 1, maxHeight: 240, overflowY: "auto" }}>
            {recents.map((h, i) => (
              <ListItemButton
                key={`recent-${h.kind}-${h.id}`}
                selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(h)}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{iconFor(h.kind)}</ListItemIcon>
                <ListItemText
                  primary={h.primary}
                  secondary={
                    <>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`cmdk.kind.${h.kind}`)}
                        sx={{ mr: 1 }}
                      />
                      {h.secondary && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          {h.secondary}
                        </Typography>
                      )}
                    </>
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
      {hits.length > 0 && (
        <List sx={{ pt: 0, pb: 1, maxHeight: 360, overflowY: "auto" }}>
          {hits.map((h, i) => (
            <ListItemButton
              key={`${h.kind}-${h.id}`}
              selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(h)}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>{iconFor(h.kind)}</ListItemIcon>
              <ListItemText
                primary={h.primary}
                secondary={
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(`cmdk.kind.${h.kind}`)}
                      sx={{ mr: 1 }}
                    />
                    {h.secondary && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {h.secondary}
                      </Typography>
                    )}
                  </>
                }
                slotProps={{ secondary: { component: "div" } }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
      {debouncedQ && !search.isLoading && hits.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("cmdk.empty")}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
